import { createElement, isHTMLElement, randomNumGenerator } from "../../../core/js/utilities";
import {
    createStore,
    reducers,
    actions,
    dispatchEvent,
    renderState,
} from "../../../core/js/state-machine";
import {
    componentName,
    componentClasses,
    icons,
    eventMap,
    defaultProps,
    schema,
    views,
} from "../../../core/components/tag/tag";
import validateInitialization from "../../../core/js/helpers/validate-initialization";

/**
 * The Tag requires to have a button or an a element
 * within the main wrapper element.
 * @param {HTMLElement} Tag main wrapper
 * @returns {Boolean}
 */
const validateHTML = (element) => {
    return isHTMLElement(element.querySelector("button,a"));
};

/**
 * Initialize the Tag component
 * @param {HTMLElement} element main Tag wrapper
 * @param {defaultProps} props optional settings.
 * @returns {{
 *  element: HTMLElement,
 *  dismiss: Function,
 *  dispose: Function
 * }} public api object
 */
const Tag = (element, props = {}) => {
    const [success, mergedProps] = validateInitialization({
        componentName,
        defaultProps,
        element,
        props,
        schema,
        validateHTML,
    });
    if (!success) return;

    /**
     * Creates the dismiss button with all
     * a11y settings
     * @returns {HTMLElement}
     */
    const getDismissButton = () => {
        return createElement("button", {
            html: icons.close,
            type: "button",
            "aria-label": mergedProps.srDismiss,
            "aria-describedby": labelButton.id,
            "aria-disabled": isInactive,
        });
    };

    /**
     * When the user clicks the close-x button,
     * removes the component from the dom and
     * dispatches the dismiss event.
     */
    const dismiss = () => {
        dispatchEvent(eventMap[actions.dismiss().type], element);
        element.parentNode.removeChild(element);
        dispose();
    };

    /**
     * Remove all listeners and the public api
     * from the **element**.
     * It makes possible to initialize same element again.
     */
    const dispose = () => {
        dismissButton && dismissButton.removeEventListener("click", handleClick, { once: true });
        element[componentName] = null;
        element = null;
    };

    /**
     * Captures the state change and dismiss
     * the tag if needed.
     * @param {Boolean} visible the new state from store.
     */
    const handleStateChange = (visible) => {
        if (!visible) {
            api.dismiss();
        }
    };

    /**
     * Captures the click and dispatch
     * the action to change the current state.
     */
    const handleClick = () => {
        dispatch(actions.dismiss());
    };

    const { dispatch, subscribe } = createStore(reducers.boolReducer, true);
    subscribe(handleStateChange);

    const labelButton = element.querySelector("button,a");
    // If label button doesn't have an Id create one for a11y reasons
    if (!labelButton.id) {
        labelButton.id = `${componentName.toLowerCase()}-${randomNumGenerator()}`;
    }

    // If element has class "inactive" render inactive label button.
    const isInactive = element.classList.contains(componentClasses.inactive);
    isInactive && renderState(views.inactive, labelButton);

    // Determine if our user wants a Tag dismiss button.
    let dismissButton;
    if (mergedProps.dismiss) {
        dismissButton = getDismissButton();
        element.appendChild(dismissButton);
        if (!isInactive) {
            dismissButton.addEventListener("click", handleClick, { once: true });
        }
    }

    const api = {
        element,
        dismiss,
        dispose,
    };

    // Assignment the api to the element
    element[componentName] = api;

    // Return the public api;
    return api;
};

export default Tag;
